import React, { useState, useEffect } from 'react';
import { config } from '../config';

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'services', label: 'Services' },
    { id: 'sermons', label: 'Sermons' },
    { id: 'events', label: 'Events' },
    { id: 'ministries', label: 'Ministries' },
    { id: 'giving', label: 'Give' },
    { id: 'register', label: 'Register' },
    { id: 'contact', label: 'Contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sections = navLinks.map(link => document.getElementById(link.id));
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        if (section && section.offsetTop <= scrollPosition && (section.offsetTop + section.offsetHeight) > scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
      setActiveSection(id);
    }
  };

  return (
    <nav 
      id="main-nav" 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/80 backdrop-blur-xl shadow-lg py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="container-custom">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center gap-4 cursor-pointer group" onClick={() => scrollToSection('home')}>
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 blur-lg rounded-full scale-0 group-hover:scale-150 transition-transform duration-500"></div>
              <img 
                src={config.logo}
                alt={config.church_name}
                loading="eager"
                decoding="async"
                className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border border-white/20 shadow-lg"
              />
            </div>
            <div>
              <h1 className={`font-heading text-lg sm:text-xl font-bold transition-colors duration-300 ${
                isScrolled ? 'text-secondary' : 'text-white'
              }`}>
                {config.church_name}
              </h1>
              <p className={`text-[10px] tracking-[0.2em] uppercase font-semibold transition-colors duration-300 ${
                isScrolled ? 'text-primary' : 'text-primary-light'
              }`}>
                {config.tagline}
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map(link => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`nav-link ${
                  isScrolled ? 'text-secondary' : 'text-white'
                } ${activeSection === link.id ? 'active' : ''}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-full transition-colors duration-300 ${
              isScrolled ? 'bg-secondary/5 text-secondary hover:bg-secondary/10' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-secondary/60 backdrop-blur-sm z-[60] transition-opacity duration-500 ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>

      {/* Mobile Menu Content */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-accent z-[70] shadow-2xl transform transition-transform duration-500 ease-out ${
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="p-8">
          <div className="flex justify-between items-center mb-12">
            <img src={config.logo} alt="Logo" loading="lazy" decoding="async" className="w-12 h-12 rounded-full" />
            <button 
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-secondary hover:text-primary transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex flex-col gap-6">
            {navLinks.map(link => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`w-full text-left text-2xl font-heading font-bold rounded-2xl px-4 py-3 border transition-all ${
                  activeSection === link.id
                    ? 'text-primary bg-primary/10 border-primary/30'
                    : 'text-secondary bg-secondary/5 border-secondary/10 hover:text-primary hover:bg-secondary/10'
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>
          <div className="mt-20 pt-8 border-t border-secondary/10">
            <p className="text-sm text-secondary/60 mb-4">Connect with us</p>
            <div className="flex gap-4">
              {/* Social icons could go here */}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
